import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import PaginationWithState from "components/Pagination/PaginationWithState";
import MShipmentReport from "../MShipmentReport/MShipmentReport";

export default function MShipments() {
  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [shipments, setShipments] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    b_e_no: "",
    port: "",
    b_e_date: "",
    expiryDate: "",
  });
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [loading, setLoading] = useState(false);

  let defaultStartDate = () => {
    const now = new Date();
    return new Date(now.getFullYear() - 1, now.getMonth() - 1, 2)
      .toISOString()
      .slice(0, 10)
      .replace("T", " ");
  };

  let defaultEndDate = () => {
    return new Date().toISOString().slice(0, 10).replace("T", " ");
  };

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilters, setDateFilters] = useState({
    b_e_date_start: "",
    b_e_date_end: "",
    expiry_date_start: "",
    expiry_date_end: "",
    // b_e_date_start: defaultStartDate(),
    // b_e_date_end: defaultEndDate(),
    // expiry_date_start: defaultStartDate(),
    // expiry_date_end: defaultEndDate(),
  });

  /*   All Functions
   ********************************************* */
  //get Data (uses POST /manufacturer/shipments?page=&limit=)
  const getShipments = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/manufacturer/shipments?page=" + usedPage + "&limit=" + pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
            ...dateFilters,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setShipments(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, searchTerm, pageLimit, filterBy.value, dateFilters]
  );

  // Delete Shipment
  const deleteShipments = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/manufacturer/shipment/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        // refresh current page
        await getShipments();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          })
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        })
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Adds Button Function
  const handleShipments = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.post("/manufacturer/shipment", formData, {
        headers: authHeader(),
      });
      if (data?.success) {
        setFormData({
          b_e_no: "",
          port: "",
          b_e_date: "",
          expiryDate: "",
        });
        setShowAddModal(false);
        setPageNumber(1);
        getShipments({ page: 1 });
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }

    setShowAddModal(false);
  };

  // Update Button Function
  const handleUpdateShipments = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.patch("/manufacturer/shipment", formData, {
        headers: authHeader(),
      });
      if (data?.success) {
        setIsUpdateMode(false);
        setShowAddModal(false);
        // refresh list
        getShipments();
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Sorting Up
  const sortUp = async (id) => {
    try {
      const { data } = await API.post(
        "/manufacturer/shipment-sort-up",
        { shipmentsId: id },
        { headers: authHeader() }
      );
      if (data.success) {
        await getShipments();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Up
  const sortDown = async (id) => {
    try {
      const { data } = await API.post(
        "/manufacturer/shipment-sort-down",
        { shipmentsId: id },
        { headers: authHeader() }
      );
      if (data.success) {
        await getShipments();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateData = async (item) => {
    setFormData({
      ...item,
      b_e_date: item.b_e_date
        ? new Date(item.b_e_date).toISOString().split("T")[0]
        : "",
      expiryDate: item.expiryDate
        ? new Date(item.expiryDate).toISOString().split("T")[0]
        : "",
    });
    setIsUpdateMode(true);
    setShowAddModal(true);
  };

  const handleDateFilterChange = (field, value) => {
    setDateFilters({ ...dateFilters, [field]: value });
  };

  const applyDateFilters = () => {
    setPageNumber(1);
    getShipments({ page: 1 });
  };

  const clearDateFilters = () => {
    setDateFilters({
      b_e_date_start: "",
      b_e_date_end: "",
      expiry_date_start: "",
      expiry_date_end: "",
    });
    // Refresh data
    getShipments({ page: 1 });

    // setDateFilters({
    //   b_e_date_start: defaultStartDate(),
    //   b_e_date_end: defaultEndDate(),
    //   expiry_date_start: defaultStartDate(),
    //   expiry_date_end: defaultEndDate(),
    // });
    setPageNumber(1);
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getShipments(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber }
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit, filterBy]);

  /*   Table functions
   ********************************************* */
  let filters = [
    { value: "shipmentId", name: "Ship ID" },
    { value: "b_e_no", name: "B/E No" },
    { value: "port", name: "Port" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "Ship ID", value: "shipmentId" },
    { name: "B/E No", value: "b_e_no" },
    { name: "Port", value: "port" },
    { name: "B/E Date", value: "b_e_date" },
    { name: "Expiry Date", value: "expiryDate" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">{item.shipmentId}</td>
      <td className="align-middle py-1">{item.b_e_no || "-"}</td>
      <td className="align-middle py-1">{item.port || "-"}</td>
      <td className="align-middle py-1">
        {item.b_e_date ? new Date(item.b_e_date).toLocaleDateString() : "-"}
      </td>
      <td className="align-middle py-1">
        {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : "-"}
      </td>
      <td className="align-middle py-1 text-end table-action">
        <button
          disabled={loading}
          onClick={() => {
            handleUpdateData(item);
          }}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>

        <DeleteModalButton
          btnClass={"btn btn-sm  px-1"}
          deleteFunc={deleteShipments}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="">
      <h2 className="mb-3 ps-2">Import Shipments</h2>
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex align-items-end">
          {/* Date Range Filters */}
          <div className="d-flex gap-2 me-2">
            <div className="d-flex flex-column">
              <small className="text-muted mb-1">B/E Date Range</small>
              <div className="d-flex gap-1">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  placeholder="Start"
                  value={dateFilters.b_e_date_start}
                  onChange={(e) =>
                    handleDateFilterChange("b_e_date_start", e.target.value)
                  }
                  style={{ width: "140px" }}
                />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  placeholder="End"
                  value={dateFilters.b_e_date_end}
                  onChange={(e) =>
                    handleDateFilterChange("b_e_date_end", e.target.value)
                  }
                  style={{ width: "140px" }}
                />
              </div>
            </div>
            <div className="d-flex flex-column">
              <small className="text-muted mb-1">Expiry Date Range</small>
              <div className="d-flex gap-1">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  placeholder="Start"
                  value={dateFilters.expiry_date_start}
                  onChange={(e) =>
                    handleDateFilterChange("expiry_date_start", e.target.value)
                  }
                  style={{ width: "140px" }}
                />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  placeholder="End"
                  value={dateFilters.expiry_date_end}
                  onChange={(e) =>
                    handleDateFilterChange("expiry_date_end", e.target.value)
                  }
                  style={{ width: "140px" }}
                />
              </div>
            </div>
            <button
              className="btn btn-sm btn-primary align-self-end"
              onClick={applyDateFilters}
              title="Apply Date Filters"
              disabled={loading}
            >
              <i className="mdi mdi-filter me-1"></i>Filter
            </button>
            {(dateFilters.b_e_date_start ||
              dateFilters.b_e_date_end ||
              dateFilters.expiry_date_start ||
              dateFilters.expiry_date_end) && (
              <button
                className="btn btn-sm btn-outline-danger align-self-end"
                onClick={clearDateFilters}
                title="Clear Date Filters"
              >
                <i className="mdi mdi-close"></i>
              </button>
            )}
          </div>
          <div>{buttons()}</div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getShipments({ query: searchTerm, page: 1 });
              } else {
                getShipments({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getShipments({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
          <div className="">
            <button
              onClick={() => {
                setShowAddModal(true);
                setFormData({});
                setIsUpdateMode(false);
              }}
              type="button"
              className="btn btn-primary  ms-1"
            >
              Add Shipment
            </button>
          </div>
          <MShipmentReport />
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={shipments}
            disableSorting={false}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/*   Add Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>{isUpdateMode ? "Update Shipment" : "Add Shipment"}</h4>
          </div>
          <hr className="mt-0" />
          <form
            onSubmit={isUpdateMode ? handleUpdateShipments : handleShipments}
            className=""
          >
            <div className="mb-2">
              <label className="">B/E No</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.b_e_no || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, b_e_no: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter B/E No"
                  required
                />
              </div>
            </div>
            <div className="mb-2">
              <label className="">Port</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.port || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, port: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Port"
                  required
                />
              </div>
            </div>
            <div className="mb-2">
              <label className="">B/E Date</label>
              <div className="">
                <input
                  type="date"
                  value={formData?.b_e_date || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, b_e_date: e.target.value });
                  }}
                  className="form-control"
                  required
                />
              </div>
            </div>
            <div className="mb-2">
              <label className="">Expiry Date</label>
              <div className="">
                <input
                  type="date"
                  value={formData?.expiryDate || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, expiryDate: e.target.value });
                  }}
                  className="form-control"
                  required
                />
              </div>
            </div>

            {/*   buttons
             ********************************************* */}
            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
